import { SearchOutlined } from "@ant-design/icons";
import {
  Card,
  Col,
  Form,
  Input,
  Row,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { Button, DatePicker, Select, Table } from "src/components";
import {
  CommissionSearchItem,
  useListAffiliateCampaigns,
  useSearchAffiliateCommissions,
} from "src/hooks/affiliate";

const { Title, Text } = Typography;

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("DD/MM/YYYY") : "-";

const formatAmount = (value?: number | null) =>
  value == null ? "-" : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatPercent = (value?: number | null) =>
  value == null ? "-" : `${value}%`;

interface FilterFormValues {
  playerCodeOrPhone?: string;
  campaignCode?: string;
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

interface ActiveFilters {
  playerCodeOrPhone?: string;
  campaignCode?: string;
  startDate?: string;
  endDate?: string;
}

const AffiliateCampaignCommissions: React.FunctionComponent = () => {
  const [filters, setFilters] = React.useState<ActiveFilters>({});
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const { data, isFetching } = useSearchAffiliateCommissions({
    playerCodeOrPhone: filters.playerCodeOrPhone || "",
    campaignCode: filters.campaignCode,
    startDate: filters.startDate,
    endDate: filters.endDate,
    page,
    pageSize,
  });

  const { data: campaignList } = useListAffiliateCampaigns();
  const campaignOptions = (campaignList?.campaigns || []).map((c) => ({
    value: c.campaignCode,
    label: `${c.campaignCode} - ${c.campaignName}`,
  }));

  const handleSearch = (values: FilterFormValues) => {
    const [start, end] = values.dateRange || [];
    setFilters({
      playerCodeOrPhone: values.playerCodeOrPhone?.trim(),
      campaignCode: values.campaignCode?.trim() || undefined,
      startDate: start ? start.format("YYYY-MM-DD") : undefined,
      endDate: end ? end.format("YYYY-MM-DD") : undefined,
    });
    setPage(1);
  };

  const columns: TableColumnsType<CommissionSearchItem> = [
    {
      title: "แคมเปญ",
      key: "campaign",
      width: 200,
      render: (_, record) => (
        <>
          <Tag color="blue">{record.campaignCode || "-"}</Tag>
          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
            {record.campaignName}
          </div>
        </>
      ),
    },
    {
      title: "ดาวน์ไลน์ (รหัส / เบอร์)",
      key: "downline",
      width: 200,
      render: (_, record) => (
        <>
          <div>{record.psCode || "-"}</div>
          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
            {record.phone || "-"}
          </div>
        </>
      ),
    },
    {
      title: "ชั้น (Tier)",
      dataIndex: "tierNumber",
      key: "tierNumber",
      width: 70,
      align: "center",
    },
    {
      title: "ยอดฐาน",
      dataIndex: "baseAmount",
      key: "baseAmount",
      width: 130,
      align: "right",
      render: formatAmount,
    },
    {
      title: "อัตราชั้น",
      dataIndex: "tierCommissionRate",
      key: "tierCommissionRate",
      width: 100,
      align: "right",
      render: formatPercent,
    },
    {
      title: "อัตรา Addon",
      dataIndex: "addonCommissionRate",
      key: "addonCommissionRate",
      width: 110,
      align: "right",
      render: formatPercent,
    },
    {
      title: "คอมมิชชัน",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      width: 140,
      align: "right",
      render: (v?: number | null) => <strong>{formatAmount(v)}</strong>,
      sorter: (a, b) => (a.commissionAmount || 0) - (b.commissionAmount || 0),
    },
    {
      title: "วันที่คำนวณ",
      dataIndex: "calculationDate",
      key: "calculationDate",
      width: 140,
      render: formatDate,
      sorter: (a, b) =>
        dayjs(a.calculationDate || 0).diff(dayjs(b.calculationDate || 0)),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>ค้นหาคอมมิชชันรายลูกค้า</Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form layout="vertical" onFinish={handleSearch} autoComplete="off">
          <Row gutter={[16, 0]} align="bottom">
            <Col span={6}>
              <Form.Item
                label="รหัส หรือ เบอร์โทร"
                name="playerCodeOrPhone"
                rules={[{ required: true, message: "ระบุรหัส หรือเบอร์โทร" }]}
              >
                <Input placeholder="PS123456789 หรือ 0812345678" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="แคมเปญ" name="campaignCode">
                <Select
                  allowClear
                  showSearch
                  width="100%"
                  optionFilterProp="label"
                  options={campaignOptions}
                  placeholder="เลือกแคมเปญ (ไม่บังคับ)"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="ช่วงวันที่คำนวณ" name="dateRange">
                <DatePicker.RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={isFetching}
                  style={{ width: "100%" }}
                >
                  ค้นหา
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {!filters.playerCodeOrPhone && (
        <Card>
          <Text type="secondary">
            กรอกรหัส หรือเบอร์โทรลูกค้าด้านบน แล้วกดค้นหา
          </Text>
        </Card>
      )}

      {filters.playerCodeOrPhone && (
        <Card
          size="small"
          title={`ผลลัพธ์ (${data?.total ?? 0} รายการ)`}
        >
          <Table
            rowKey="uid"
            loading={isFetching}
            columns={columns}
            dataSource={data?.items || []}
            pagination={{
              current: page,
              pageSize,
              total: data?.total ?? 0,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            scroll={{ x: 1500 }}
          />
        </Card>
      )}
    </div>
  );
};

export default AffiliateCampaignCommissions;
